import { useState, useEffect } from 'react';
import FleetManagerLogin from './FleetManagerLogin';
import DriverLogin from './DriverLogin';

type Step = 'select' | 'driver_login' | 'fleet_login';

export default function RoleSelectionScreen() {
  const [step, setStep] = useState<Step>('select');

  useEffect(() => {
    setStep('select');
  }, []);

  if (step === 'driver_login') return <DriverLogin onBack={() => setStep('select')} />;
  if (step === 'fleet_login') return <FleetManagerLogin onBack={() => setStep('select')} />;

  return (
    <div
      className="fixed inset-0 z-[997] flex flex-col overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0d1b3e 0%, #1a3a8f 45%, #2563eb 100%)' }}
    >
      <div className="absolute top-[-80px] right-[-60px] w-72 h-72 rounded-full pointer-events-none" style={{ background: 'rgba(255,255,255,0.04)' }} />
      <div className="absolute bottom-[-60px] left-[-80px] w-80 h-80 rounded-full pointer-events-none" style={{ background: 'rgba(255,255,255,0.03)' }} />

      {/* Header */}
      <div className="pt-16 pb-6 px-6 text-center relative z-10">
        <div
          className="w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-2xl overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)', border: '1.5px solid rgba(255,255,255,0.2)' }}
        >
          <img
            src="https://storage.readdy-site.link/project_files/90f45ec7-b0ce-4237-a8d9-2b6895d1e519/1ed3c010-ddb6-4196-9984-6ab18a486563_ChatGPT-Image-26-juin-2025-15_05_07.png?v=23bbf812ca6f957bf20c41ebf2939508"
            alt="AutoLink Logo"
            className="w-full h-full object-cover"
          />
        </div>
        <h1 className="text-white font-extrabold text-2xl tracking-tight mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>
          AutoLink
        </h1>
        <p className="text-blue-300 text-sm">Conduisez mieux, gérez plus</p>
      </div>

      {/* Role cards */}
      <div className="flex-1 px-5 pb-2 flex flex-col justify-center gap-4 relative z-10">

        {/* Conducteur */}
        <button
          onClick={() => setStep('driver_login')}
          className="w-full text-left rounded-3xl p-5 transition-all active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, rgba(59,130,246,0.25) 0%, rgba(99,102,241,0.2) 100%)',
            border: '1.5px solid rgba(99,130,246,0.4)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
              <i className="ri-steering-2-line text-white text-2xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="text-white font-bold text-base">Conducteur</h3>
                <span className="text-[9px] bg-blue-500/30 text-blue-200 px-2 py-0.5 rounded-full font-semibold">EMPLOYÉ</span>
              </div>
              <p className="text-blue-200 text-xs leading-snug">Suivi kilométrique, trajets pro/privé, rapports</p>
            </div>
            <i className="ri-arrow-right-s-line text-white/50 text-xl"></i>
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            {['Trajets', 'Rapports', 'Profil', 'Suivi'].map(tag => (
              <span key={tag} className="text-[10px] bg-white/10 text-blue-200 px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        </button>

        {/* Fleet Manager */}
        <button
          onClick={() => setStep('fleet_login')}
          className="w-full text-left rounded-3xl p-5 transition-all active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.15) 100%)',
            border: '1.5px solid rgba(16,185,129,0.35)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
              <i className="ri-shield-keyhole-line text-white text-2xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="text-white font-bold text-base">Gestionnaire de flotte</h3>
                <span className="text-[9px] bg-emerald-500/30 text-emerald-200 px-2 py-0.5 rounded-full font-semibold">ADMIN</span>
              </div>
              <p className="text-emerald-200 text-xs leading-snug">Gestion de flotte, conducteurs, validations, exports</p>
            </div>
            <i className="ri-lock-line text-white/40 text-base"></i>
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            {['Flotte', 'Conducteurs', 'Rapports', 'Admin'].map(tag => (
              <span key={tag} className="text-[10px] bg-white/10 text-emerald-200 px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        </button>
      </div>

      {/* Footer */}
      <div className="px-6 pb-10 pt-3 flex flex-col items-center gap-3 relative z-10">
        <div className="flex items-center justify-center gap-2">
          <i className="ri-shield-check-line text-blue-400 text-[10px]"></i>
          <span className="text-blue-400 text-[10px]">Données chiffrées</span>
          <span className="text-white/20">·</span>
          <i className="ri-lock-2-line text-blue-400 text-[10px]"></i>
          <span className="text-blue-400 text-[10px]">Accès sécurisé</span>
          <span className="text-white/20">·</span>
          <span className="text-blue-400 text-[10px]">RGPD</span>
        </div>
      </div>
    </div>
  );
}
